import { useQueryClient } from "@tanstack/react-query";
import { useRef, useState } from "react";
import {
	useLocation,
	useNavigate,
	useParams,
	useSearchParams,
} from "react-router-dom";
import { toast } from "sonner";
import { useChatMessagesQuery } from "web_myplann/Api/Chat/useChatMessagesQuery";
import { useSendImageMutation } from "web_myplann/Api/Chat/useSendImageMutation";
import { useSendMessageMutation } from "web_myplann/Api/Chat/useSendMessageMutation";
import { dropOptimisticChatMessages } from "web_myplann/Chat/Helpers/chatMessagesCache";
import { getQueuedChatClientIds } from "web_myplann/Chat/Helpers/getQueuedChatClientIds";
import { useChatAttachments } from "web_myplann/Chat/Hooks/useChatAttachments";
import { useChatRecipient } from "web_myplann/Chat/Hooks/useChatRecipient";
import { useMarkChatMessagesRead } from "web_myplann/Chat/Hooks/useMarkChatMessagesRead";
import { Routing } from "web_myplann/Infrastructure/Routing/Routing";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { createId } from "web_myplann/Shared/Utils/createId";
import { getTypedParam } from "web_myplann/Shared/Utils/getTypedParam";

type Args = {
	onMessageSent: () => void;
};

export type ChatPageState = {
	draftMessage?: string;
};

export const useChatPage = ({ onMessageSent }: Args) => {
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();
	const { state } = useLocation() as { state: ChatPageState | null };
	const queryClient = useQueryClient();
	const { t } = useResource();
	const routeParams = useParams<{
		conversationId: string;
	}>();

	const [conversationId, setConversationId] = useState(
		getTypedParam(routeParams, "conversationId", Number) ?? null,
	);
	const conversationIdRef = useRef(conversationId);

	const cleanedRef = useRef(false);
	if (!cleanedRef.current) {
		cleanedRef.current = true;
		dropOptimisticChatMessages(
			queryClient,
			conversationId,
			getQueuedChatClientIds(queryClient, conversationId),
		);
	}

	const advisorIdParam = getTypedParam(searchParams, "advisorId", String);
	const {
		advisorId,
		advisorName,
		advisorPhoto,
		isLoading: isRecipientLoading,
	} = useChatRecipient(conversationId, advisorIdParam);

	const [input, setInput] = useState(state?.draftMessage ?? "");

	const {
		data: messages = [],
		isLoading,
		hasNextPage,
		fetchNextPage,
		isFetchingNextPage,
	} = useChatMessagesQuery(conversationId);
	useMarkChatMessagesRead(conversationId, messages);

	const { mutate: sendMessage } = useSendMessageMutation(conversationId);
	const { mutate: sendImage } = useSendImageMutation(conversationId);

	const takeCaption = () => {
		const caption = input.trim();
		setInput("");

		return caption;
	};

	const sendText = (content: string, clientId: string) => {
		sendMessage(
			{
				conversationId: conversationIdRef.current,
				advisorId,
				content,
				clientId,
			},
			{
				onSuccess: (result) => {
					const newId = result.conversationId;
					if (conversationIdRef.current === null && newId > 0) {
						conversationIdRef.current = newId;
						setConversationId(newId);
						navigate(Routing.Chat.Detail.path(String(newId), advisorId), {
							replace: true,
						});
					}
				},
				onError: () => {
					toast.error(t(Resources.Chat.sendError));
				},
			},
		);
	};

	const sendAttachment = (
		file: File,
		caption: string,
		clientId: string,
		{ onSettled }: { onSettled: () => void },
	) => {
		const targetConversationId = conversationIdRef.current;
		if (targetConversationId === null) return;

		sendImage(
			{
				conversationId: targetConversationId,
				file,
				content: caption,
				clientId,
			},
			{
				onSuccess: () => {
					onSettled();
				},
				onError: () => {
					toast.error(t(Resources.Chat.sendError));
				},
			},
		);
	};

	const { onRetry, onPickPhoto, onPickGallery, onPickFile } =
		useChatAttachments({
			conversationIdRef,
			takeCaption,
			sendText,
			sendAttachment,
			onMessageSent,
		});

	const handleSend = () => {
		if (!input.trim()) return;

		sendText(takeCaption(), createId());
		onMessageSent();
	};

	return {
		advisorName,
		advisorPhoto,
		messages,
		isLoading: isLoading || isRecipientLoading,
		hasOlder: hasNextPage,
		isLoadingOlder: isFetchingNextPage,
		loadOlder: () => fetchNextPage(),
		input,
		setInput,
		onSend: handleSend,
		onRetry,
		onPickPhoto,
		onPickGallery,
		onPickFile,
		onBack: () => navigate(-1),
	};
};
